import {
    Component,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { BeforeTrainingStart } from '../../../shared/interfaces/before-training-start';
import {
    ExpertDatasetSelectionDTO,
    ExpertDatasetUsageType,
} from '../../models/expert-data';
import { ExpertDatasetService } from '../../services/expert-dataset.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-expert-dataset',
    templateUrl: './expert-dataset.component.html',
    styleUrls: ['./expert-dataset.component.scss'],
    standalone: false,
})
export class ExpertDatasetComponent
    extends BeforeTrainingStart
    implements OnInit, OnDestroy, OnChanges
{
    @Input() trainingId: string;
    @Input() editingIsDisabled: boolean;

    expertDatasets: string[];
    dataLoaded: boolean = true;
    subscriptions: Subscription[] = [];
    usageType: ExpertDatasetUsageType;
    changedState: boolean;
    @ViewChild('toggle') toggle: MatSlideToggle;
    dropdownControl: FormControl<string> = new FormControl(null);

    constructor(
        private expertDatasetService: ExpertDatasetService,
        private authservice: AuthenticationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.expertDatasetService
            .getExpertDataById(this.trainingId)
            .subscribe((expertDataset: ExpertDatasetSelectionDTO) => {
                this.expertDatasets = expertDataset.availableOptions;
                if (this.expertDatasets.length === 0) {
                    this.toggle?.setDisabledState(true);
                }
                this.setExpertDatasetValue(
                    expertDataset.expertDatasetDto?.datasetName
                );
                this.usageType = expertDataset.expertDatasetUsageType;
                if (this.usageType === ExpertDatasetUsageType.NO_EXPERT_DATA) {
                    this.dropdownControl.disable();
                }

                if (this.editingIsDisabled) {
                    this.toggle.setDisabledState(true);
                    this.dropdownControl.disable();
                }
                this.changedState = false;
            });
        this.subscriptions.push(
            this.authservice.logoutTriggered$.subscribe(() => {
                if (this.changedState) {
                    this.changedState = false;
                    this.expertDatasetService
                        .saveExpertDataSelection(
                            this.trainingId,
                            this.dropdownControl.value,
                            this.usageType
                        )
                        .subscribe(() => {
                            this.authservice.logoutPreparationsFinished$.next(
                                true
                            );
                        });
                } else {
                    this.authservice.logoutPreparationsFinished$.next(true);
                }
            })
        );
    }

    saveBeforeStart(): Observable<any> {
        if (this.changedState && !this.editingIsDisabled) {
            this.changedState = false;
            return this.expertDatasetService.saveExpertDataSelection(
                this.trainingId,
                this.dropdownControl.value,
                this.usageType
            );
        }
        return of(null);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes.editingIsDisabled &&
            !changes.editingIsDisabled.isFirstChange()
        ) {
            if (this.editingIsDisabled) {
                this.toggle.setDisabledState(true);
                this.dropdownControl.disable();
            } else {
                if (this.expertDatasets.length !== 0) {
                    this.toggle.setDisabledState(false);
                }
                if (this.usageType !== ExpertDatasetUsageType.NO_EXPERT_DATA) {
                    this.dropdownControl.enable();
                }
            }
        }
    }

    setExpertDatasetValue(expertData: any) {
        this.dropdownControl.setValue(expertData);
        this.changedState = true;
    }

    onToggle() {
        if (this.usageType === ExpertDatasetUsageType.NO_EXPERT_DATA) {
            this.usageType = ExpertDatasetUsageType.ONLY_EXPERT_DATA;
            this.dropdownControl.enable();
        } else {
            this.usageType = ExpertDatasetUsageType.NO_EXPERT_DATA;
            this.dropdownControl.disable();
        }
        this.changedState = true;
    }

    ngOnDestroy(): void {
        if (this.changedState && !this.editingIsDisabled) {
            this.changedState = false;
            this.expertDatasetService
                .saveExpertDataSelection(
                    this.trainingId,
                    this.dropdownControl.value,
                    this.usageType
                )
                .subscribe();
        }

        this.subscriptions.forEach((subscription: Subscription) => {
            subscription.unsubscribe();
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event) {
        if (
            this.changedState &&
            !this.editingIsDisabled &&
            this.expertDatasetService
        ) {
            this.changedState = false;
            this.expertDatasetService
                .saveExpertDataSelection(
                    this.trainingId,
                    this.dropdownControl.value,
                    this.usageType
                )
                .subscribe();
            event.preventDefault();
            event.returnValue = false;
        }
    }
}
